import type { I18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';

import { formatCivilDate, formatInstant } from '@/shared/format/date';
import { formatCents } from '@/shared/format/money';

// Os formatadores do legado que o detalhe da operação usa para montar texto (descrição da garantia,
// datasets de bureau, prazo). Cada um cita a função original; onde o legado devolve lixo para valor
// ausente (`moment(undefined)` = hoje, `formatDocumentNumber('undefined')`), aqui sai `null` e quem
// chama mostra "N/D" (desvio registrado no E8B-DEPARA-RELATORIO).

const HAS_OFFSET = /(?:Z|[+-]\d{2}:?\d{2})$/i;

/**
 * `formatDate` (helpers/formats/DateFormat.ts:265): `moment(date).format('DD/MM/YYYY')`, no fuso do
 * navegador. Com offset, o dia do instante em São Paulo (o fuso de quem opera); sem offset, a data
 * como foi escrita.
 */
export function legacyDate(value: unknown, locale = 'pt-BR'): string | null {
  if (typeof value !== 'string' || value.trim() === '') return null;
  const text = value.trim();
  if (text.includes('T') && HAS_OFFSET.test(text)) return formatInstant(text, { locale, time: false });
  return formatCivilDate(text, { locale }) ?? formatInstant(text, { locale, time: false });
}

/** `new Date(v).toLocaleString('pt-BR')` (data e hora) dos datasets. */
export function legacyDateTime(value: unknown, locale = 'pt-BR'): string | null {
  if (typeof value !== 'string' || value.trim() === '') return null;
  return formatInstant(value.trim(), { locale, seconds: true });
}

/** `formatMonthYear` (helpers/formats/DocumentNumber.ts:24-33): `202509` → `09/2025`; `09/2025` fica. */
export function legacyMonthYear(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  if (/^\d{2}\/\d{4}$/.test(value)) return value;
  if (!/^\d{6}$/.test(value)) return null;
  const month = Number(value.slice(4, 6));
  if (month < 1 || month > 12) return null;
  return `${value.slice(4, 6)}/${value.slice(0, 4)}`;
}

/** `formatCurrencyInCents` (helpers/formats/Currency.ts:32-46): centavos → BRL; vazio vira R$ 0,00. */
export function legacyCents(value: unknown, locale = 'pt-BR'): string {
  const number = typeof value === 'string' && value.trim() !== '' ? Number(value) : value;
  if (typeof number !== 'number' || !Number.isFinite(number)) return formatCents(0, { locale });
  return formatCents(Math.round(number), { locale });
}

/** `maskCurrencyInCents` (Currency.ts:63-71): o valor já em reais. */
export function legacyReais(value: unknown, locale = 'pt-BR'): string {
  if (typeof value !== 'number' || !Number.isFinite(value) || value === 0) return formatCents(0, { locale });
  return formatCents(Math.round(value * 100), { locale });
}

/**
 * `formatDocumentNumber` (DocumentNumber.ts:3-22): pontua pelo tamanho, **sem conferir o dígito**
 * (o documento que vem do bureau ou do empregador aparece como veio, D10). CNPJ alfanumérico com
 * zeros à esquerda até 14, como o legado.
 */
export function legacyDocument(value: unknown): string | null {
  if (typeof value !== 'string' && typeof value !== 'number') return null;
  const text = String(value).trim();
  if (text === '' || text.includes('*')) return text === '' ? null : text;
  const cleaned = text.replace(/[^a-zA-Z0-9]/g, '');
  if (/[a-zA-Z]/.test(cleaned)) {
    if (cleaned.length > 14) return text;
    const padded = cleaned.toUpperCase().padStart(14, '0');
    return `${padded.slice(0, 2)}.${padded.slice(2, 5)}.${padded.slice(5, 8)}/${padded.slice(8, 12)}-${padded.slice(12, 14)}`;
  }
  if (cleaned.length <= 11) return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  if (cleaned.length <= 14) return cleaned.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  return text;
}

/** `formatPhoneNumber` sem o `replacePhoneNumber` (PhoneNumber.ts:8-14): só os dígitos, `(DD) `, corta em 14 e hífen. */
export function legacyPhoneNumber(value: unknown): string {
  if (typeof value !== 'string' || value === '') return '';
  const digits = value.replace(/\D/g, '');
  return digits
    .replace(/(\d{2})(\d)/, '($1) $2')
    .slice(0, 14)
    .replace(/(\d)(\d{4})$/, '$1-$2');
}

/** `formatPercentage(v, casas, multiplicar)` (Currency.ts:73-82): `toFixed` com vírgula; 0 → `0%`. */
export function legacyPercentage(value: unknown, places = 4, multiplyBy100 = true): string {
  if (typeof value !== 'number' || !Number.isFinite(value) || value === 0) return '0%';
  return `${(multiplyBy100 ? value * 100 : value).toFixed(places).replace('.', ',')}%`;
}

/**
 * `formatPercentageWithPlaces` (Currency.ts:111-117): fração × 100 truncada (não arredondada) em
 * `casas`, deslocando a vírgula no texto para não perder a última casa no ponto flutuante.
 */
export function legacyPercentageTruncated(value: unknown, places = 4): string {
  const numeric = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numeric) || numeric === 0) return '0%';
  const [mantissa = '0', exponent = '0'] = Math.abs(numeric).toString().split(/e/i);
  const [integerDigits = '0', fractionDigits = ''] = mantissa.split('.');
  const shifted = integerDigits.length + 2 + Number(exponent);
  const leftPad = Math.max(0, 1 - shifted);
  const point = shifted + leftPad;
  const digits = `${'0'.repeat(leftPad)}${integerDigits}${fractionDigits}`.padEnd(point + places, '0');
  const integerPart = digits.slice(0, point).replace(/^0+(?=\d)/, '');
  const fractionPart = digits.slice(point, point + places);
  const sign = numeric < 0 ? '-' : '';
  return `${sign}${integerPart}${places > 0 ? `,${fractionPart}` : ''}%`;
}

/** `formatMultiply` (Currency.ts:19-27): fração × 100 (0 quando vazio). */
export function legacyMultiply(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) && value !== 0 ? value * 100 : 0;
}

// ─── prazo da operação (helpers/formats/DateFormat.ts:24-137) ───────────────

interface TermParts {
  readonly years: number;
  readonly months: number;
  readonly days: number;
}

const BRASILIA_OFFSET_MS = 3 * 60 * 60 * 1000;

/** `toCalendarParts` (DateFormat.ts:10-20): o dia no calendário de Brasília (UTC-3) por aritmética. */
function calendarParts(value: string | number | Date): { year: number; month: number; day: number } | null {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const brasilia = new Date(date.getTime() - BRASILIA_OFFSET_MS);
  return { year: brasilia.getUTCFullYear(), month: brasilia.getUTCMonth(), day: brasilia.getUTCDate() };
}

/** `dateDiff` (DateFormat.ts:24-97): anos, meses e dias entre início e vencimento (fim ausente = agora). */
export function legacyDateDiff(start: unknown, end: unknown, now: number = Date.now()): TermParts | null {
  if (typeof start !== 'string' || start === '') return null;
  let from = calendarParts(start);
  let to = calendarParts(typeof end === 'string' && end !== '' ? end : now);
  if (!from || !to) return null;
  const after = from.year > to.year || (from.year === to.year && (from.month > to.month || (from.month === to.month && from.day > to.day)));
  if (after) [from, to] = [to, from];
  const february = (from.year % 4 === 0 && from.year % 100 !== 0) || from.year % 400 === 0 ? 29 : 28;
  const daysInMonth = [31, february, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  let years = to.year - from.year;
  let months = to.month - from.month;
  if (months < 0) {
    years -= 1;
    months += 12;
  }
  let days = to.day - from.day;
  if (days < 0) {
    if (months > 0) months -= 1;
    else {
      years -= 1;
      months = 11;
    }
    days += daysInMonth[from.month] ?? 30;
  }
  return { years, months, days };
}

export interface TermInput {
  readonly paymentPeriodicity?: { readonly periodicity?: string | null | undefined } | null | undefined;
  readonly periodicity?: string | null | undefined;
  readonly numberOfPayments?: number | null | undefined;
  readonly startDate?: string | null | undefined;
  readonly dueDate?: string | null | undefined;
}

/**
 * `resolveOperationTerm` (DateFormat.ts:108-122): mensal com parcelas → parcelas em anos e meses; senão a
 * diferença entre início e vencimento. `null` = "N/D".
 */
export function legacyTerm(input: TermInput | null | undefined, now: number = Date.now()): TermParts | null {
  const periodicity = input?.paymentPeriodicity?.periodicity || input?.periodicity;
  const payments = input?.numberOfPayments;
  if (periodicity?.toLowerCase() === 'monthly' && payments) return { years: Math.floor(payments / 12), months: payments % 12, days: 0 };
  const diff = legacyDateDiff(input?.startDate, input?.dueDate, now);
  if (!diff) return null;
  // `dateDiff` mostra dias só quando não há ano nem mês.
  return diff;
}

/** O texto do prazo como o legado escreve ("2 anos e 3 meses", "18 meses", "12 dias"). */
export function legacyTermText(i18n: I18n, parts: TermParts | null): string {
  if (!parts) return i18n._(msg`N/D`);
  const { years, months, days } = parts;
  const yearText = years === 1 ? i18n._(msg`${years} ano`) : i18n._(msg`${years} anos`);
  const monthText = months === 1 ? i18n._(msg`${months} mês`) : i18n._(msg`${months} meses`);
  if (years > 0 && months > 0) return i18n._(msg`${yearText} e ${monthText}`);
  if (years > 0) return yearText;
  if (months > 0) return monthText;
  return days === 1 ? i18n._(msg`${days} dia`) : i18n._(msg`${days} dias`);
}

/** `replacePhoneNumber` + `formatPhoneNumber` (PhoneNumber.ts:1-14): tira o `+55` e põe `(dd) nnnnn-nnnn`, cortado em 14. */
function legacyPhone(value: unknown): string {
  const raw = piece(value);
  if (raw === '') return '';
  const digits = raw.replace('+55', '').replace(/\D/g, '');
  return digits.replace(/(\d{2})(\d)/, '($1) $2').slice(0, 14).replace(/(\d)(\d{4})$/, '$1-$2');
}

type Account = Readonly<Record<string, unknown>>;
const piece = (value: unknown): string => (typeof value === 'string' ? value : typeof value === 'number' ? String(value) : '');

/**
 * `formatBankAccountForOption` + `genericFormatPixKey` (helpers/formats/BankAccount.ts:17-38, 116-146): o
 * rótulo da conta de liquidação e da conta para troco como o select do legado mostra.
 */
export function legacyBankAccountLabel(i18n: I18n, account: unknown): string | null {
  if (account === null || typeof account !== 'object') return null;
  const item = account as Account;
  const nd = i18n._(msg`N/D`);
  const bankCode = item['bankCode'];
  const bank = bankCode === '0' || bankCode === 0 || bankCode === undefined || bankCode === null ? '' : `${piece(bankCode)} - `;
  const bankName = piece(item['bankCodeDisplay']) || nd;
  const agencyDigit = item['agencyDigit'] ? `- ${piece(item['agencyDigit'])}` : '';
  const accountDigit = item['accountDigit'] ? `- ${piece(item['accountDigit'])}` : '';
  const agency = piece(item['agency']);
  const number = piece(item['account']);
  if (item['operationTypeValue'] === 'Transfer') {
    return i18n._(msg`Transferência: ${bank} ${bankName} - Ag: ${agency} ${agencyDigit} - Conta: ${number}${accountDigit}`).replace(/\s+/g, ' ').trim();
  }
  if (item['operationTypeValue'] !== 'Pix') return null;
  const key = item['keyPix'];
  const doc = legacyDocument(key) ?? nd;
  const keyText = piece(key) || nd;
  switch (item['pixKeyTypeValue']) {
    case 'NaturalRegistrationNumber':
      return i18n._(msg`Pix: CPF - ${doc}`);
    case 'LegalRegistrationNumber':
      return i18n._(msg`Pix: CNPJ - ${doc}`);
    case 'Phone': {
      const keyText = legacyPhone(key);
      return i18n._(msg`Pix: Telefone - ${keyText}`);
    }
    case 'Email':
      return i18n._(msg`Pix: Email - ${keyText}`);
    case 'Automatic':
      return i18n._(msg`Pix: Automática - ${keyText}`);
    case 'AgencyAndAccount': {
      const accountNumber = piece(item['account']) || piece(item['accountNumber']);
      return i18n._(msg`Pix: Banco: ${bank} ${bankName} | Ag: ${agency} ${agencyDigit} | Conta: ${accountNumber} ${accountDigit}`).replace(/\s+/g, ' ').trim();
    }
    default:
      return null;
  }
}
